import React, { useState } from "react";

/**
 * Shows where one figure came from and how far it can be trusted.
 *
 * @param {{provenance: object|null}} props - Provenance recorded with the figure.
 * @returns {JSX.Element|null} The badge, or nothing when provenance is absent.
 *
 * @remarks
 * An empty sourceUrl is meaningful rather than missing — it marks a figure the
 * model reasoned to instead of read, which is exactly the distinction an
 * auditor needs. The badge says so plainly rather than hiding the difference.
 */
const SourceConfidenceBadge = ({ provenance }) => {
  const [open, setOpen] = useState(false);

  if (!provenance) return null;

  const estimated = provenance.dataType === "Estimated";

  return (
    <span className="aica-badge-wrap">
      <button
        type="button"
        className={`aica-badge ${estimated ? "is-estimated" : ""}`}
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
      >
        {estimated ? "Estimated" : "Source"} &middot; {provenance.confidence}
      </button>

      {open && (
        <span className="aica-badge-pop" role="dialog">
          <span className="aica-badge-row">
            <strong>Document</strong>
            {provenance.source || "Not recorded"}
          </span>
          <span className="aica-badge-row">
            <strong>Period</strong>
            {provenance.period || "Not recorded"}
          </span>
          <span className="aica-badge-row">
            <strong>Basis</strong>
            {estimated
              ? "Estimated by the AI, not read from a disclosure"
              : "Reported by the company"}
          </span>
          <span className="aica-badge-row">
            <strong>Confidence</strong>
            {provenance.confidence}
          </span>
          {provenance.sourceUrl ? (
            <a
              className="aica-badge-link"
              href={provenance.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open source document
            </a>
          ) : (
            <span className="aica-badge-note">
              No source document — this figure was estimated.
            </span>
          )}
        </span>
      )}
    </span>
  );
};

export default SourceConfidenceBadge;
